import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import IngresosDrawer from './IngresosDrawer';
import IngresoModal from './IngresoModal';
import GastosDrawer from './GastosDrawer';
import GastoModal from './GastoModal';
import DeudasDrawer from './DeudasDrawer';
import DeudaModal from './DeudaModal';
import { EyeIcon, EyeOffIcon } from '../components/Icons';
import { onDataChanged } from '../utils/dataBus';

const formatMoneyRaw = (n) => '$' + (n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const MASK = '$ ••••';
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';

export default function PanelContable({ embedded = false }) {
  const [ingresos, setIngresos] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [deudas, setDeudas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerIng, setDrawerIng] = useState(false);
  const [drawerMes, setDrawerMes] = useState(null);
  const [modalCargarIng, setModalCargarIng] = useState(false);
  const [drawerGas, setDrawerGas] = useState(false);
  const [drawerGasMes, setDrawerGasMes] = useState(null);
  const [modalCargarGas, setModalCargarGas] = useState(false);
  const [drawerDeu, setDrawerDeu] = useState(false);
  const [modalCargarDeu, setModalCargarDeu] = useState(false);
  const [fichaIngreso, setFichaIngreso] = useState(null);
  const [fichaGasto, setFichaGasto] = useState(null);
  const [fichaDeuda, setFichaDeuda] = useState(null);
  const [editingIngreso, setEditingIngreso] = useState(null);
  const [editingGasto, setEditingGasto] = useState(null);
  const [editingDeuda, setEditingDeuda] = useState(null);
  const [evolucionAbierta, setEvolucionAbierta] = useState(false);
  const [ocultar, setOcultar] = useState(() => localStorage.getItem('contable_ocultar') === '1');

  const formatMoney = (n) => ocultar ? MASK : formatMoneyRaw(n);
  const toggleOcultar = () => setOcultar(v => { localStorage.setItem('contable_ocultar', v ? '0' : '1'); return !v; });

  const abrirDrawerIng = (mes = null) => { setDrawerMes(mes); setDrawerIng(true); };
  const abrirDrawerGas = (mes = null) => { setDrawerGasMes(mes); setDrawerGas(true); };

  const refrescar = () => { fetchIngresos(); fetchGastos(); fetchDeudas(); };

  useEffect(() => {
    Promise.all([fetchIngresos(), fetchGastos(), fetchDeudas()])
      .finally(() => setLoading(false));
  }, []);

  // Mantenerse al día ante cualquier escritura en estas tablas, venga de
  // donde venga (otro drawer, el bot de WhatsApp, un RPC de borrado).
  useEffect(() => onDataChanged((d) => {
    if (d.table === 'ingresos') fetchIngresos();
    else if (d.table === 'gastos') fetchGastos();
    else if (d.table === 'deudas') fetchDeudas();
  }, ['ingresos', 'gastos', 'deudas']), []);

  async function fetchIngresos() {
    const { data } = await supabase.from('ingresos')
      .select('id, monto, fecha, concepto, cliente_nombre, medio_cobro, clientes(nombre, apellido)')
      .order('fecha', { ascending: false });
    setIngresos(data || []);
  }

  async function fetchGastos() {
    const { data } = await supabase.from('gastos')
      .select('id, monto, fecha, concepto, categoria, tipo')
      .order('fecha', { ascending: false });
    setGastos(data || []);
  }

  async function fetchDeudas() {
    const { data } = await supabase.from('deudas')
      .select('id, concepto, monto_total, monto_cobrado, estado, fecha_vencimiento, clientes(nombre, apellido), deudor_nombre')
      .order('fecha_vencimiento', { ascending: true, nullsFirst: false });
    setDeudas(data || []);
  }

  // ── Cálculos ──

  const hoy = new Date();
  const mesActual = hoy.toISOString().substring(0, 7); // "2026-06"

  const ingresosMesLista = ingresos.filter(i => (i.fecha || '').startsWith(mesActual));
  const ingresosMes = ingresosMesLista.reduce((s, i) => s + (i.monto || 0), 0);
  const gastosMesLista = gastos.filter(g => (g.fecha || '').startsWith(mesActual));
  const gastosMes   = gastosMesLista.reduce((s, g) => s + (g.monto || 0), 0);
  const balanceMes  = ingresosMes - gastosMes;

  const deudasVigentes = deudas.filter(d => d.estado !== 'cobrada' && d.estado !== 'incobrable');
  const saldoPendiente = deudasVigentes.reduce((s, d) => s + Math.max(0, (d.monto_total || 0) - (d.monto_cobrado || 0)), 0);

  // Últimos 6 meses para la tabla evolutiva
  const ultimos6Meses = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - (5 - i), 1);
    const key = d.toISOString().substring(0, 7);
    const label = d.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
    return { key, label };
  });

  const evolucion = ultimos6Meses.map(({ key, label }) => ({
    label,
    ingresos: ingresos.filter(i => (i.fecha || '').startsWith(key)).reduce((s, i) => s + (i.monto || 0), 0),
    gastos: gastos.filter(g => (g.fecha || '').startsWith(key)).reduce((s, g) => s + (g.monto || 0), 0),
  }));

  // Deudas por vencer en los próximos 30 días
  const en30dias = new Date(hoy); en30dias.setDate(en30dias.getDate() + 30);
  const todayStr = hoy.toISOString().split('T')[0];
  const en30Str = en30dias.toISOString().split('T')[0];
  const deudasPorVencer = deudasVigentes.filter(d => d.fecha_vencimiento && d.fecha_vencimiento >= todayStr && d.fecha_vencimiento <= en30Str);
  const deudasVencidas = deudasVigentes.filter(d => d.fecha_vencimiento && d.fecha_vencimiento < todayStr);

  if (loading) return (
    <div className={embedded ? 'page-embedded' : 'page'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text4)' }}>
      Cargando panel contable...
    </div>
  );

  return (
    <div className={embedded ? 'page-embedded' : 'page'}>
      <div className="page-content" style={{ paddingTop: 10, paddingBottom: 10, display: 'flex', flexDirection: 'column' }}>

        {/* ── Encabezado compacto: mes + privacidad + fecha ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {hoy.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' }).toUpperCase()}
            </span>
            <button
              onClick={toggleOcultar}
              title={ocultar ? 'Mostrar cifras' : 'Ocultar cifras'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0, padding: 2, color: 'var(--text4)', display: 'flex', alignItems: 'center' }}
            >
              {ocultar ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
            </button>
          </div>
          <span style={{ fontSize: 12, color: 'var(--text4)' }}>
            {hoy.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12, flexShrink: 0 }}>
          <KpiCard label="Ingresos del mes" value={formatMoney(ingresosMes)} accent="#22c55e" sub={`${ingresos.filter(i => (i.fecha||'').startsWith(mesActual)).length} registros`} onClick={() => abrirDrawerIng()} />
          <KpiCard label="Gastos del mes"   value={formatMoney(gastosMes)}   accent="#ef4444" sub={`${gastosMesLista.length} registros`} onClick={() => abrirDrawerGas()} />
          <KpiCard
            label="Balance del mes"
            value={formatMoney(Math.abs(balanceMes))}
            accent={balanceMes >= 0 ? '#3b82f6' : '#ef4444'}
            sub={balanceMes >= 0 ? '▲ Superávit' : '▼ Déficit'}
            valueColor={balanceMes >= 0 ? '#3b82f6' : '#ef4444'}
          />
          <KpiCard label="Deudas pendientes" value={formatMoney(saldoPendiente)} accent="#f59e0b" sub={`${deudasVigentes.length} deudas vigentes`} onClick={() => setDrawerDeu(true)} />
        </div>

        {/* ── Evolución 6 meses (gráfico, tras el resultado del mes) ── */}
        <div style={{ marginBottom: 10, flexShrink: 0 }}>
          <button
            onClick={() => setEvolucionAbierta(v => !v)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '9px 14px', cursor: 'pointer', marginBottom: evolucionAbierta ? 10 : 0 }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Evolución últimos 6 meses</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text4)', transition: 'transform 0.2s', transform: evolucionAbierta ? 'rotate(180deg)' : 'none' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {evolucionAbierta && (() => {
            const maxEvol = Math.max(1, ...evolucion.flatMap(m => [m.ingresos, m.gastos]));
            const CH = 130;
            return (
              <div className="card" style={{ padding: '18px 18px 12px' }}>
                <div style={{ display: 'flex', gap: 18, marginBottom: 16 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#22c55e' }} />Ingresos</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)' }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#ef4444' }} />Gastos</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: CH }}>
                  {evolucion.map(m => {
                    const hi = m.ingresos > 0 ? Math.max(3, (m.ingresos / maxEvol) * CH) : 0;
                    const hg = m.gastos > 0 ? Math.max(3, (m.gastos / maxEvol) * CH) : 0;
                    return (
                      <div key={m.label} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 5, height: '100%' }}>
                        <div title={`Ingresos: ${formatMoney(m.ingresos)}`} style={{ width: 16, height: hi, background: 'linear-gradient(180deg,#4ade80,#22c55e)', borderRadius: '4px 4px 0 0' }} />
                        <div title={`Gastos: ${formatMoney(m.gastos)}`} style={{ width: 16, height: hg, background: 'linear-gradient(180deg,#f87171,#ef4444)', borderRadius: '4px 4px 0 0' }} />
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--border)' }}>
                  {evolucion.map(m => {
                    const bal = m.ingresos - m.gastos;
                    return (
                      <div key={m.label} style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'capitalize' }}>{m.label}</div>
                        <div style={{ fontSize: 11, fontFamily: 'DM Mono', fontWeight: 700, color: bal >= 0 ? '#3b82f6' : '#ef4444', marginTop: 2 }}>{bal >= 0 ? '' : '−'}{formatMoney(Math.abs(bal))}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── Últimos movimientos ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr)', gap: 20, marginBottom: 12, flex: 1, minHeight: 0 }}>

          {/* Últimos ingresos */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Últimos ingresos</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => abrirDrawerIng()} style={{ fontSize: 11 }}>Ver ingresos</button>
                <button className="btn btn-primary btn-sm" onClick={() => setModalCargarIng(true)} style={{ fontSize: 11 }}>Cargar ingreso</button>
              </div>
            </div>
            <div className="card" style={{ padding: 0, overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {ingresosMesLista.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--text4)' }}>Sin ingresos este mes</div>
              ) : ingresosMesLista.slice(0, 10).map(i => {
                const cliente = i.clientes ? `${i.clientes.apellido || ''} ${i.clientes.nombre || ''}`.trim() : i.cliente_nombre || '—';
                const medioBg = { Efectivo: '#dcfce7', Transferencia: '#dbeafe' }[i.medio_cobro] || '#f3f4f6';
                const medioColor = { Efectivo: '#15803d', Transferencia: '#1d4ed8' }[i.medio_cobro] || '#6b7280';
                return (
                  <div key={i.id} onClick={() => setFichaIngreso(i)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.concepto || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text4)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                        <span>{cliente}</span>
                        <span>·</span>
                        <span>{formatDate(i.fecha)}</span>
                        <span style={{ background: medioBg, color: medioColor, padding: '0 6px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{i.medio_cobro}</span>
                      </div>
                    </div>
                    <div style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 700, color: '#22c55e', flexShrink: 0 }}>{formatMoney(i.monto)}</div>
                  </div>
                );
              })}
              </div>
              <button
                onClick={() => abrirDrawerIng('todos')}
                style={{ display: 'block', width: '100%', background: 'none', border: 'none', borderTop: '0.5px solid var(--border)', cursor: 'pointer', color: 'var(--text4)', fontSize: 11, padding: '10px 16px', textAlign: 'center' }}
              >
                Mostrar ingresos de meses anteriores
              </button>
            </div>
          </div>

          {/* Últimos gastos */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Últimos gastos</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => abrirDrawerGas()} style={{ fontSize: 11 }}>Ver gastos</button>
                <button className="btn btn-primary btn-sm" onClick={() => setModalCargarGas(true)} style={{ fontSize: 11 }}>Cargar gasto</button>
              </div>
            </div>
            <div className="card" style={{ padding: 0, overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {gastosMesLista.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--text4)' }}>Sin gastos este mes</div>
              ) : gastosMesLista.slice(0, 10).map(g => {
                const tipoBg = { eventual: '#eff6ff', fijo: '#f3f4f6', deuda: '#fefce8' }[g.tipo] || '#f3f4f6';
                const tipoColor = { eventual: '#3b82f6', fijo: '#6b7280', deuda: '#ca8a04' }[g.tipo] || '#6b7280';
                return (
                  <div key={g.id} onClick={() => setFichaGasto(g)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.concepto || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text4)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                        {g.categoria && <span>{g.categoria}</span>}
                        {g.categoria && <span>·</span>}
                        <span>{formatDate(g.fecha)}</span>
                        <span style={{ background: tipoBg, color: tipoColor, padding: '0 6px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{g.tipo}</span>
                      </div>
                    </div>
                    <div style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 700, color: '#ef4444', flexShrink: 0 }}>{formatMoney(g.monto)}</div>
                  </div>
                );
              })}
              </div>
              <button
                onClick={() => abrirDrawerGas('todos')}
                style={{ display: 'block', width: '100%', background: 'none', border: 'none', borderTop: '0.5px solid var(--border)', cursor: 'pointer', color: 'var(--text4)', fontSize: 11, padding: '10px 16px', textAlign: 'center' }}
              >
                Mostrar gastos de meses anteriores
              </button>
            </div>
          </div>
        </div>

        {/* ── Deudas alertas + vigentes ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr)', gap: 20, marginBottom: 12, flex: 1, minHeight: 0 }}>

          {/* Deudas vencidas o por vencer */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, minHeight: 26 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Alertas de deudas</span>
            </div>
            <div className="card" style={{ padding: 0, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {deudasVencidas.length === 0 && deudasPorVencer.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--text4)' }}>Sin alertas — todo al día</div>
              ) : (
                <>
                  {deudasVencidas.map(d => {
                    const nombre = d.clientes ? `${d.clientes.apellido || ''} ${d.clientes.nombre || ''}`.trim() : d.deudor_nombre || '—';
                    const saldo = (d.monto_total || 0) - (d.monto_cobrado || 0);
                    return (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '0.5px solid var(--border)', background: '#fff5f5' }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.concepto}</div>
                          <div style={{ fontSize: 11, color: 'var(--text4)' }}>{nombre} · Venció {formatDate(d.fecha_vencimiento)}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 700, color: '#ef4444' }}>{formatMoney(saldo)}</div>
                          <div style={{ fontSize: 10, color: '#ef4444' }}>VENCIDA</div>
                        </div>
                      </div>
                    );
                  })}
                  {deudasPorVencer.map(d => {
                    const nombre = d.clientes ? `${d.clientes.apellido || ''} ${d.clientes.nombre || ''}`.trim() : d.deudor_nombre || '—';
                    const saldo = (d.monto_total || 0) - (d.monto_cobrado || 0);
                    const diasRestantes = Math.round((new Date(d.fecha_vencimiento + 'T12:00:00') - hoy) / 86400000);
                    return (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '0.5px solid var(--border)', background: '#fffbeb' }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.concepto}</div>
                          <div style={{ fontSize: 11, color: 'var(--text4)' }}>{nombre} · Vence {formatDate(d.fecha_vencimiento)}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>{formatMoney(saldo)}</div>
                          <div style={{ fontSize: 10, color: '#f59e0b' }}>en {diasRestantes}d</div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {/* Deudas vigentes */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, minHeight: 26 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.6 }}>Deudas vigentes ({deudasVigentes.length})</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setDrawerDeu(true)} style={{ fontSize: 11 }}>Ver deudas</button>
                <button className="btn btn-primary btn-sm" onClick={() => setModalCargarDeu(true)} style={{ fontSize: 11 }}>Cargar deuda</button>
              </div>
            </div>
            <div className="card" style={{ padding: 0, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {deudasVigentes.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--text4)' }}>Sin deudas vigentes</div>
              ) : deudasVigentes.map(d => {
                const nombre = d.clientes ? `${d.clientes.apellido || ''} ${d.clientes.nombre || ''}`.trim() : d.deudor_nombre || '—';
                const saldo = Math.max(0, (d.monto_total || 0) - (d.monto_cobrado || 0));
                const pct = d.monto_total > 0 ? Math.min(100, ((d.monto_cobrado || 0) / d.monto_total) * 100) : 0;
                const estadoColor = { vigente: '#f59e0b', 'en gestión': '#3b82f6', cobrada: '#22c55e', incobrable: '#ef4444' }[d.estado] || '#9ca3af';
                return (
                  <div key={d.id} onClick={() => setFichaDeuda(d)} style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                      <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.concepto}</div>
                        <div style={{ fontSize: 11, color: 'var(--text4)' }}>{nombre}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontFamily: 'DM Mono', fontSize: 13, fontWeight: 700, color: estadoColor }}>{formatMoney(saldo)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text4)' }}>de {formatMoney(d.monto_total)}</div>
                      </div>
                    </div>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#22c55e' : '#3b82f6', borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>

      <IngresosDrawer
        open={drawerIng}
        initialMes={drawerMes}
        ocultar={ocultar}
        onClose={() => setDrawerIng(false)}
        onSaved={() => { fetchIngresos(); fetchDeudas(); }}
      />

      <IngresoModal
        open={modalCargarIng}
        editing={null}
        onClose={() => setModalCargarIng(false)}
        onSaved={() => { fetchIngresos(); fetchDeudas(); }}
      />

      <GastosDrawer
        open={drawerGas}
        initialMes={drawerGasMes}
        ocultar={ocultar}
        onClose={() => setDrawerGas(false)}
        onSaved={() => { fetchGastos(); }}
      />

      <GastoModal
        open={modalCargarGas}
        editing={null}
        onClose={() => setModalCargarGas(false)}
        onSaved={() => { fetchGastos(); }}
      />

      <DeudasDrawer
        open={drawerDeu}
        ocultar={ocultar}
        onClose={() => setDrawerDeu(false)}
        onSaved={refrescar}
      />

      <DeudaModal
        open={modalCargarDeu}
        editing={null}
        onClose={() => setModalCargarDeu(false)}
        onSaved={() => { fetchDeudas(); }}
      />

      {/* Ficha Ingreso */}
      {fichaIngreso && !editingIngreso && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setFichaIngreso(null)}>
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Ingreso</div>
              <button className="modal-close" onClick={() => setFichaIngreso(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FichaRow label="Monto" value={formatMoneyRaw(fichaIngreso.monto)} highlight />
                <FichaRow label="Fecha" value={formatDate(fichaIngreso.fecha)} />
                {fichaIngreso.concepto && <FichaRow label="Concepto" value={fichaIngreso.concepto} />}
                {(fichaIngreso.cliente_nombre || fichaIngreso.clientes) && (
                  <FichaRow label="Cliente" value={fichaIngreso.clientes ? `${fichaIngreso.clientes.apellido || ''} ${fichaIngreso.clientes.nombre || ''}`.trim() : fichaIngreso.cliente_nombre} />
                )}
                {fichaIngreso.medio_cobro && <FichaRow label="Medio de cobro" value={fichaIngreso.medio_cobro} />}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setFichaIngreso(null)}>Cerrar</button>
              <button className="btn btn-primary" onClick={() => { setEditingIngreso(fichaIngreso); setFichaIngreso(null); }}>Editar</button>
            </div>
          </div>
        </div>
      )}

      <IngresoModal
        open={!!editingIngreso}
        editing={editingIngreso}
        onClose={() => setEditingIngreso(null)}
        onSaved={() => { setEditingIngreso(null); fetchIngresos(); fetchDeudas(); }}
      />

      {/* Ficha Gasto */}
      {fichaGasto && !editingGasto && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setFichaGasto(null)}>
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Gasto</div>
              <button className="modal-close" onClick={() => setFichaGasto(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <FichaRow label="Monto" value={formatMoneyRaw(fichaGasto.monto)} highlight />
                <FichaRow label="Fecha" value={formatDate(fichaGasto.fecha)} />
                {fichaGasto.concepto && <FichaRow label="Concepto" value={fichaGasto.concepto} />}
                {fichaGasto.categoria && <FichaRow label="Categoría" value={fichaGasto.categoria} />}
                {fichaGasto.tipo && <FichaRow label="Tipo" value={fichaGasto.tipo} />}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setFichaGasto(null)}>Cerrar</button>
              <button className="btn btn-primary" onClick={() => { setEditingGasto(fichaGasto); setFichaGasto(null); }}>Editar</button>
            </div>
          </div>
        </div>
      )}

      <GastoModal
        open={!!editingGasto}
        editing={editingGasto}
        onClose={() => setEditingGasto(null)}
        onSaved={() => { setEditingGasto(null); fetchGastos(); }}
      />

      {/* Ficha Deuda */}
      {fichaDeuda && !editingDeuda && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setFichaDeuda(null)}>
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Deuda / Honorario pendiente</div>
              <button className="modal-close" onClick={() => setFichaDeuda(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {fichaDeuda.concepto && <FichaRow label="Concepto" value={fichaDeuda.concepto} />}
                <FichaRow label="Deudor" value={fichaDeuda.clientes ? `${fichaDeuda.clientes.apellido || ''} ${fichaDeuda.clientes.nombre || ''}`.trim() : fichaDeuda.deudor_nombre || '—'} />
                <FichaRow label="Total" value={formatMoneyRaw(fichaDeuda.monto_total)} highlight />
                <FichaRow label="Cobrado" value={formatMoneyRaw(fichaDeuda.monto_cobrado || 0)} />
                <FichaRow label="Saldo" value={formatMoneyRaw(Math.max(0, (fichaDeuda.monto_total || 0) - (fichaDeuda.monto_cobrado || 0)))} />
                <FichaRow label="Estado" value={fichaDeuda.estado} />
                {fichaDeuda.fecha_vencimiento && <FichaRow label="Vencimiento" value={formatDate(fichaDeuda.fecha_vencimiento)} />}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setFichaDeuda(null)}>Cerrar</button>
              <button className="btn btn-primary" onClick={() => { setEditingDeuda(fichaDeuda); setFichaDeuda(null); }}>Editar</button>
            </div>
          </div>
        </div>
      )}

      <DeudaModal
        open={!!editingDeuda}
        editing={editingDeuda}
        onClose={() => setEditingDeuda(null)}
        onSaved={() => { setEditingDeuda(null); fetchDeudas(); }}
      />
    </div>
  );
}

function FichaRow({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
      <span style={{ fontSize: 12, color: 'var(--text4)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: highlight ? 18 : 14, fontWeight: highlight ? 700 : 400, color: highlight ? 'var(--text)' : 'var(--text2)' }}>{value}</span>
    </div>
  );
}

function KpiCard({ label, value, accent, sub, valueColor, onClick }) {
  return (
    <div
      className="stat-card"
      style={{ position: 'relative', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default', padding: '12px 16px' }}
      onClick={onClick}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: accent, borderRadius: '8px 0 0 8px' }} />
      <div style={{ paddingLeft: 8 }}>
        <div className="stat-label">{label}</div>
        <div className="stat-value" style={{ color: valueColor || 'var(--text)', fontSize: 20 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>{sub}</div>}
      </div>
      {onClick && <div style={{ position: 'absolute', top: 8, right: 10, fontSize: 14, color: 'var(--text4)' }}>›</div>}
    </div>
  );
}
